import { Link } from 'react-router-dom';
import { Heart, MapPin, Star, Users, Trees } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { TID, formatKurus, type MansionCard as Card } from '@yalidavet/shared';
import { coverFor } from '@/lib/images';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface Props {
  mansion: Card;
  /** Appends the active search params so the detail page opens pre-filled. */
  searchQuery?: string;
}

export default function MansionCard({ mansion, searchQuery }: Props) {
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [fav, setFav] = useState(!!mansion.isFavorite);

  const favMutation = useMutation({
    mutationFn: (next: boolean) => api.favorite(mansion.id, next),
    onSuccess: (res) => {
      setFav(res.isFavorite);
      qc.invalidateQueries({ queryKey: ['favorites'] });
    },
    onError: () => setFav((v) => !v), // rollback optimistic flip
  });

  const toggleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isSignedIn) {
      navigate('/giris');
      return;
    }
    const next = !fav;
    setFav(next); // optimistic
    favMutation.mutate(next);
  };

  const href = `/mulk/${mansion.slug}${searchQuery ? `?${searchQuery}` : ''}`;
  const ineligible = mansion.eligibility && !mansion.eligibility.eligible;

  return (
    <article
      className={cn('yd-card group flex flex-col transition-all duration-500 hover:shadow-burgundy hover:-translate-y-2 hover:scale-[1.02]', ineligible && 'opacity-60')}
      data-testid={TID.mansionCard(mansion.id)}
      data-testid-generic={TID.mansionCardAny}
      data-mansion-id={mansion.id}
      data-garden-m2={mansion.gardenSizeM2}
      data-max-capacity={mansion.maxCapacity}
      data-price-kurus={mansion.dailyPriceKurus}
    >
      <Link to={href} className="relative block aspect-[4/3] overflow-hidden bg-cream-300">
        <img
          src={mansion.coverUrl || coverFor(mansion.id)}
          alt={mansion.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />

        {mansion.isFeatured && (
          <div className="absolute left-0 top-4 bg-burgundy-800/95 px-4 py-1.5 font-display text-[11px] uppercase tracking-[0.2em] text-cream-100">
            Popüler
          </div>
        )}

        <button
          type="button"
          onClick={toggleFavorite}
          aria-label={fav ? 'Favorilerden çıkar' : 'Favorilere ekle'}
          aria-pressed={fav}
          data-testid={TID.cardFavorite(mansion.id)}
          data-favorite={fav}
          className="absolute right-3 top-3 grid h-10 w-10 place-items-center rounded-lg bg-white/95 shadow-burgundy transition-all duration-300 hover:bg-white hover:shadow-gold hover:scale-110 active:scale-95"
        >
          <Heart className={cn('h-5 w-5 transition-all duration-300', fav ? 'fill-burgundy-600 text-burgundy-600 animate-scale-in' : 'text-yali-700')} />
        </button>
      </Link>

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-1 font-display font-semibold text-burgundy-900">
            <Link to={href} className="hover:text-gold-600 transition">{mansion.title}</Link>
          </h3>
          <span className="flex shrink-0 items-center gap-1 text-sm bg-gold-100 px-2 py-1 rounded" data-testid={TID.cardRating}>
            <Star className="h-4 w-4 fill-gold-500 text-gold-500" aria-hidden />
            <span className="font-bold text-gold-900">{mansion.ratingAvg.toFixed(1)}</span>
            <span className="text-gold-700 text-xs">({mansion.ratingCount})</span>
          </span>
        </div>

        <p className="flex items-center gap-1.5 text-sm text-yali-700 font-medium">
          <MapPin className="h-4 w-4 text-burgundy-500" aria-hidden />
          <span className="text-yali-900">{mansion.district}, {mansion.city}</span>
        </p>

        <div className="flex flex-wrap items-center gap-4 text-xs text-yali-700 font-medium border-t border-cream-200 pt-3">
          <span className="flex items-center gap-1.5 bg-cream-100 px-2 py-1 rounded-full">
            <Users className="h-4 w-4 text-burgundy-600" aria-hidden /> 
            <strong>{mansion.maxCapacity}</strong> kişi
          </span>
          <span className="flex items-center gap-1.5 bg-cream-100 px-2 py-1 rounded-full">
            <Trees className="h-4 w-4 text-burgundy-600" aria-hidden /> 
            <strong>{mansion.gardenSizeM2} m²</strong> bahçe
          </span>
        </div>

        {mansion.eventTags.length > 0 && (
          <ul className="flex flex-wrap gap-2 pt-2">
            {mansion.eventTags.slice(0, 3).map((tag) => (
              <li key={tag} className="yd-tag bg-gold-100 text-gold-800 border border-gold-200" data-testid={TID.cardEventTag}>
                {tag}
              </li>
            ))}
            {mansion.eventTags.length > 3 && (
              <li className="yd-tag bg-cream-200 text-yali-700 border border-cream-300">+{mansion.eventTags.length - 3}</li>
            )}
          </ul>
        )}

        {ineligible && (
          <p className="rounded-lg bg-burgundy-50 px-3 py-2 text-xs text-burgundy-800 border border-burgundy-200 font-medium" data-testid="card-ineligible">
            ⚠️ {mansion.eligibility!.messageTr}
          </p>
        )}

        <p
          className="mt-auto pt-4 border-t border-cream-200"
          data-testid={TID.cardPrice}
          data-price-kurus={mansion.dailyPriceKurus}
          data-weekend-price-kurus={mansion.weekendPriceKurus}
        >
          <span className="text-2xl font-bold text-burgundy-900">{formatKurus(mansion.dailyPriceKurus)}</span>
          <span className="text-sm text-yali-600 ml-1">/ gece</span>
          <div className="text-xs text-gold-700 font-medium mt-1">
            Hafta sonu: <strong>{formatKurus(mansion.weekendPriceKurus)}</strong>
          </div>
        </p>
      </div>
    </article>
  );
}
